package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * Where a key is used in the code, harvested from the repository the CLI is already reading.
 *
 * **The market's most-praised translator feature is in-context editing, and we have nothing like
 * it.** Screenshots, OCR and alt-click editors all depend on somebody attaching an image, which is
 * why their coverage is never complete — and none of them is a thing this product could build
 * cheaply or honestly. This is not that feature and must never be sold as one.
 *
 * It is the version of the same problem that fits what this product actually is. Every other tool
 * has to ask for context because the code is somewhere it cannot see; `lingvo push` is already
 * standing in the repository with the file open. A translator who can read "used in
 * `checkout/CartScreen.kt:214`, inside `confirmButtonLabel`" knows the string is a button and not a
 * heading, which is most of what a screenshot was going to tell them.
 *
 * [line] is 1-based, matching every editor and every `file:line` a person has ever pasted.
 */
@Serializable
data class KeyUsageDto(
    val path: String,
    val line: Int,
    /**
     * The line itself, trimmed, for reading in place.
     *
     * Optional because it is a convenience and not the record: the path and line are what identify
     * the usage, and a snippet that has drifted from the file is worse than none. Capped at
     * [MAX_USAGE_SNIPPET_CHARS] server-side — a minified bundle has lines of 200KB, and one of them
     * would be stored, sent, and rendered into a translator's editor.
     */
    val snippet: String? = null,
)

/**
 * Replaces the recorded usages for the named keys, and only those.
 *
 * **Replace rather than merge, and scoped to the keys named.** A push knows the whole truth about
 * the files it scanned and nothing about the ones it did not, so merging would accumulate usages
 * for code that has since been deleted and never remove them — the failure every "we'll just add
 * to it" index eventually has. Sending a key with an empty list is how a scan says "this key is no
 * longer used anywhere I looked", which is a real answer and has to be expressible.
 *
 * Keyed by key *name*, not id: the CLI has names, because names are what it just read out of the
 * source file. Names the project does not know are counted and skipped rather than rejected, since
 * a scan can legitimately run against a working tree that is ahead of the last push.
 */
@Serializable
data class ReplaceKeyUsagesRequest(
    val usages: Map<String, List<KeyUsageDto>> = emptyMap(),
)

@Serializable
data class KeyUsagesReplacedDto(
    val keys: Int,
    val usages: Int,
    /** Names the scan found that this project has no key for. Reported, never an error. */
    val unknownKeys: Int,
)

/**
 * The most keys one request may carry.
 *
 * A large project has tens of thousands of keys and a scan finds most of them, so the CLI pages.
 */
const val MAX_KEYS_PER_USAGE_REQUEST = 500

/**
 * The most usages stored for one key.
 *
 * A string like "OK" appears everywhere, and the hundredth occurrence tells a translator nothing
 * the first five did not. The cap is what stops one common word turning a key row into a payload.
 */
const val MAX_USAGES_PER_KEY = 20

/** How much of a line is worth keeping. A minified bundle's line is not context. */
const val MAX_USAGE_SNIPPET_CHARS = 200

/**
 * The most one request body may weigh, and the reason [MAX_KEYS_PER_USAGE_REQUEST] is not the only
 * bound a client needs.
 *
 * **A key count is not a size.** The server refuses any request declaring more than 256KB — an
 * availability guard on a machine with a gigabyte of memory, and one this endpoint is deliberately
 * not exempt from. A page of 500 keys carrying twenty usages each is 850KB of paths, and 2.7MB with
 * snippets, so paging by key count alone produces a first request that is rejected before it is
 * read. Every real project would have hit it; no test did, because a test body is small.
 *
 * 192KB leaves room for the envelope and for a client whose size estimate is approximate, which any
 * estimate made without serialising twice must be.
 */
const val MAX_KEY_USAGE_REQUEST_BYTES = 192 * 1024
